import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, unref as _unref, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, createVNode as _createVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "hotel-infos__infos-wrap" }
const _hoisted_2 = { class: "hotel-infos__map-wrap" }

import { computed, ref, onMounted, watch } from 'vue';
import MapPreviewAndModal from '@lmt-rpb/MapPreviewAndModal/MapPreviewAndModal.vue';
import HotelGeneralDescription from '@lmt-rpb/Molecules/HotelGeneralDescription/HotelGeneralDescription.vue';
import HotelFacilitiesTeaser from '@lmt-rpb/Molecules/HotelFacilitiesTeaser/HotelFacilitiesTeaser.vue';
import HotelMap from '@lmt-rpb/Molecules/HotelMap/HotelMap.vue';
import { OFFER_BOX_TELEPORT } from '@global-js/constants';
import { useStore } from '@/components/common/store';
import type { ItemType, OfferType } from '@/interfaces/hotel-list-types/hotel-list-types';
import type { OfferListTypoHotelData } from '@/interfaces/offer-list-types/offer-list-typo-hotel-data';
import SelectContentAnalytics from '@/js/modules/analytics/SelectContentAnalytics';
import type { BaseOfferForHotelList, ApiDataTypes, ApiDataTypesKeys } from '@/components/common/types';

interface Props {
	hotelInfo: OfferListTypoHotelData;
	mapData: {
		key: string;
	};
	bestOfferType?: OfferType | null;
}

const viewBoxOffset = 0.005;

export default /*@__PURE__*/_defineComponent({
  __name: 'HotelInfos',
  props: {
    hotelInfo: {},
    mapData: {},
    bestOfferType: {}
  },
  setup(__props: any) {

const props = __props;

const store = useStore();

const searchMask = computed(() => store.state.searchMask);
const description = computed(() => props.hotelInfo.Descriptions);
const locationName = computed(() => props.hotelInfo.Location.Address.join('; '));
const hasDescriptionOrAttributes = computed(() => props.hotelInfo.Descriptions?.length || props.hotelInfo.Attributes?.length);

const blockClassModifiers = computed(() => ({
	'hotel-infos--no-infos': !hasDescriptionOrAttributes.value,
}));

const hotelToShow = computed<ItemType | null>((): BaseOfferForHotelList | null => {
	if (!props.hotelInfo || !props.bestOfferType) {
		return null;
	}
	const { Hotel, Location, NumberOfImages, RatingCount, PopularAttributes, Ratings } = props.hotelInfo;
	const offerKey: 'AccommodationOffer' | 'PackageOffer' =
		props.bestOfferType.TravelType === 'Package' ? 'PackageOffer' : 'AccommodationOffer';
	return {
		Hotel,
		Location: {
			...Location,
			City: {
				CityName: Location.City.Name,
				TtCyId: Location.City.TtCyid,
			},
			Region: {
				RegionName: Location.Region.Name,
				Climate: [],
				TtRId: Location.Region.TtRid,
			},
			Country: {
				...Location.Country,
				CountryName: Location.Country.Name,
				CountryCode: Location.Country.IsoCode,
			},
		},
		NumberOfImages,
		NumberOfRatings: RatingCount,
		PopularAttributes,
		Ratings,
		[offerKey]: {
			...props.bestOfferType,
			Board: props.bestOfferType?.BoardType,
		},
	};
});

const hotelRequestBody = ref<ApiDataTypes>();
const northwest = computed(() => ({
	lat: props.hotelInfo.Location.Latitude - viewBoxOffset,
	lng: props.hotelInfo.Location.Longitude - viewBoxOffset,
}));
const southeast = computed(() => ({
	lat: props.hotelInfo.Location.Latitude + viewBoxOffset,
	lng: props.hotelInfo.Location.Longitude + viewBoxOffset,
}));

const apiData = store.getters['searchMask/apiData'];
const showMoreFacilities = () => {
	SelectContentAnalytics.selectContent({ cId: 'hotel_facilities', cType: 'ux_hotelpage_show_details' });
};
const showDescription = () => {
	SelectContentAnalytics.selectContent({ cId: 'hotel_general_description', cType: 'ux_hotelpage_show_details' });
};
const openMap = () => {
	SelectContentAnalytics.selectContent({ cId: 'hotel_map' });
};

function initDefaultSearchSettings() {
	const hotelOnly = store.state.searchMask.onlyHotel;
	const exclude: ApiDataTypesKeys[] = hotelOnly ? ['DepartureAirport', 'DirectFlight', 'Transfer'] : [];
	exclude.push('DefiniteStartDate', 'TourOperatorCodes', 'HotelIffCode', 'Cities', 'Regions');
	const travelType = hotelOnly ? 'Accommodation' : 'Package';
	hotelRequestBody.value = apiData({ travelType }, exclude);
}

onMounted(() => {
	initDefaultSearchSettings();
});

watch(searchMask, initDefaultSearchSettings, { deep: true });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", {
    class: _normalizeClass(['hotel-infos', blockClassModifiers.value])
  }, [
    _createElementVNode("div", _hoisted_1, [
      (_ctx.hotelInfo.Attributes && _ctx.hotelInfo.Attributes?.length)
        ? (_openBlock(), _createBlock(HotelFacilitiesTeaser, {
            key: 0,
            attributes: _ctx.hotelInfo.Attributes,
            "top-seller": _ctx.hotelInfo.Hotel.TopSeller,
            rooms: _ctx.hotelInfo.Hotel.RoomCount,
            onShowMoreClick: showMoreFacilities
          }, null, 8 /* PROPS */, ["attributes", "top-seller", "rooms"]))
        : _createCommentVNode("v-if", true),
      (_ctx.hotelInfo.Descriptions && _ctx.hotelInfo.Descriptions?.length)
        ? (_openBlock(), _createBlock(HotelGeneralDescription, {
            key: 1,
            "hotel-description": description.value,
            onShowMoreButtonClick: showDescription
          }, null, 8 /* PROPS */, ["hotel-description"]))
        : _createCommentVNode("v-if", true)
    ]),
    _createElementVNode("div", _hoisted_2, [
      (hasDescriptionOrAttributes.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: _normalizeClass(_unref(OFFER_BOX_TELEPORT).HOTEL_INFOS)
          }, null, 2 /* CLASS */))
        : _createCommentVNode("v-if", true),
      (hotelRequestBody.value)
        ? (_openBlock(), _createBlock(MapPreviewAndModal, {
            key: 1,
            id: "hotel-info-map",
            class: "hotel-infos__section-google-maps",
            lat: _ctx.hotelInfo.Location.Latitude,
            lng: _ctx.hotelInfo.Location.Longitude,
            "maps-key": _ctx.mapData.key,
            marker: false,
            northwest: northwest.value,
            southeast: southeast.value,
            "show-note": false,
            "hotel-request-body": hotelRequestBody.value!,
            "hotel-to-show-iff-code": _ctx.hotelInfo?.Hotel.IffCode,
            "hotel-to-show": hotelToShow.value
          }, {
            default: _withCtx(({ loadFullscreen }) => [
              _createVNode(HotelMap, {
                location: locationName.value,
                onClickHotelMap: () => (openMap(), loadFullscreen())
              }, null, 8 /* PROPS */, ["location", "onClickHotelMap"])
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["lat", "lng", "maps-key", "northwest", "southeast", "hotel-request-body", "hotel-to-show-iff-code", "hotel-to-show"]))
        : _createCommentVNode("v-if", true),
      (!hasDescriptionOrAttributes.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 2,
            class: _normalizeClass(_unref(OFFER_BOX_TELEPORT).HOTEL_INFOS)
          }, null, 2 /* CLASS */))
        : _createCommentVNode("v-if", true)
    ])
  ], 2 /* CLASS */))
}
}

})